import React, { useEffect, useState } from 'react';
import moment from 'moment';
import { useDispatch, useSelector } from 'react-redux';
import { Box, Button, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import CustomTypography from '@src/new-components/Typography';
import { useHistory } from 'react-router-dom';
import sanitizeHtml from 'sanitize-html';
import { InfoRounded as InfoRoundedIcon } from '@mui/icons-material';
import DialogWithContent from '@src/components/DialogWithContent';
import { COLOR } from '@src/styles/color';
import CustomOptions from '@src/components/CustomOptions';
import CommonButton from '@src/new-components/Button';
import PaperFireworks from '@src/assets/images/paper-fireworks.svg';
import ReferralErrorIcon from '@src/assets/images/referral-error-noti.svg';
import { delimitNumber, formatNumber } from '@src/utils/number';
import routes from '@src/constants/route';
import actions from '@src/redux/actions';
import { redeemReferral } from '@src/apis/referral';
import i18n from '@src/languages';
import { StyledReferralDialogNote } from '../index.style';
import {
  StyledReferralRedeemContent,
  StyledReferralCannotRedeemContent,
} from './index.style';

const GiftButton = ({ maxRewardCharacter, redeemedCharacters }) => {
  const { t } = useTranslation();
  const { language } = i18n;
  const history = useHistory();
  const dispatch = useDispatch();
  const { user } = useSelector((state) => state.auth);
  const referralConfig = useSelector((state) => state.referralConfig);
  const { charsPerPoint, maxAllowedRedeemCharactersPerMonth } =
    referralConfig || {};
  const { referral, packageExpiryDate } = user;
  const { earnedPoints = 0, spentPoints = 0 } = referral || {};
  const initPoints = earnedPoints - spentPoints;

  const [value, setValue] = useState(maxRewardCharacter);
  const [openPopupReward, setOpenPopupReward] = useState(false);
  const [openPopupCannotRedeem, setOpenPopupCannotRedeem] = useState(false);

  const [isLoading, setIsLoading] = useState(false);

  const hasReachedMaxRedeemCharacter =
    redeemedCharacters === maxAllowedRedeemCharactersPerMonth;

  const handleClosePopupReward = () => {
    setOpenPopupReward(false);
    setValue(maxRewardCharacter);
  };

  const handleClosePopupCannotRedeem = () => setOpenPopupCannotRedeem(false);

  const handleClickGiftButton = () => {
    if (packageExpiryDate && moment().isAfter(packageExpiryDate))
      setOpenPopupCannotRedeem(true);
    else setOpenPopupReward(true);
  };

  const handleClickButtonBuyNow = () => {
    history.push(routes.PAYMENT);
    handleClosePopupCannotRedeem();
  };

  const handleRedeem = async () => {
    const points = value / charsPerPoint;
    setIsLoading(true);
    const response = await redeemReferral(points);
    setIsLoading(false);
    const { status } = response || {};
    if (status) {
      setOpenPopupReward(false);
      dispatch(
        actions.noti.push({
          severity: 'success',
          message: t('claimCharacterRewardsSuccess', {
            characters: delimitNumber(value, language),
          }),
        }),
      );
      dispatch(
        actions.auth.updateUserInfo({
          ...user,
          referral: {
            ...user.referral,
            spentPoints:
              user?.referral?.earnedPoints > 0
                ? (user?.referral?.spentPoints || 0) + points
                : 0,
          },
        }),
      );
    } else {
      dispatch(
        actions.noti.push({
          severity: 'error',
          message: t('errorOccurred'),
        }),
      );
    }
  };

  const renderRedeemAction = () => (
    <Button
      onClick={handleRedeem}
      variant="contained"
      className="dialog-actions-button"
      disabled={isLoading || !value}
      sx={{
        opacity: isLoading || !value ? 0.5 : 1,
        color: 'inherit',
        backgroundColor: 'primary.main',
        '&:disabled': {
          color: 'inherit',
          backgroundColor: 'primary.main',
        },
      }}
    >
      {t('claimRewards')}
    </Button>
  );

  const renderCannotRedeemAction = () => (
    <Button
      variant="contained"
      className="dialog-actions-button"
      onClick={handleClickButtonBuyNow}
    >
      {t('buyNow')}
    </Button>
  );

  const generateSelectOptions = () => {
    const minValue = 1000;
    const maxValue = maxRewardCharacter;
    const count = Math.floor((maxValue - minValue) / 1000) + 1;

    return Array.from({ length: count }, (_, i) => {
      const stepValue = minValue + i * 1000;
      return {
        label: stepValue?.toString(),
        value: stepValue,
      };
    });
  };

  const contentRewardDialog = () => (
    <StyledReferralRedeemContent
      hasReachedMaxRedeemCharacter={hasReachedMaxRedeemCharacter}
    >
      <Box className="redeem-character-wrapper">
        <Box style={{ width: '100%' }}>
          <CustomOptions
            customStyles={{
              height: '48px',
            }}
            headerOption={
              <CustomTypography
                variant="body-14-regular"
                color={COLOR.descriptionUpload}
              >
                {t('enterNumberCharactersWithdraw')}
              </CustomTypography>
            }
            value={{
              label: formatNumber(value || 0, language),
              value,
            }}
            onChange={(option) => setValue(option.value)}
            options={generateSelectOptions()}
            inputType="number"
          />
        </Box>
        <Box className="redeemed-character-wrapper">
          <Typography className="redeemed-character-text">
            {t('redeemedCharacters')}
          </Typography>
          <Typography className="redeemed-character-text">
            {delimitNumber(redeemedCharacters, language)}
          </Typography>
        </Box>
      </Box>

      <StyledReferralDialogNote>
        <InfoRoundedIcon className="note-icon" />
        <Typography
          className="note-text"
          dangerouslySetInnerHTML={{
            __html: sanitizeHtml(
              t('rewardFromReferralCodeNote', {
                maxAllowedRedeemCharacters: delimitNumber(
                  maxAllowedRedeemCharactersPerMonth,
                  language,
                ),
              }),
            ),
          }}
        />
      </StyledReferralDialogNote>
    </StyledReferralRedeemContent>
  );

  const contentCannotRedeemDialog = () => (
    <StyledReferralCannotRedeemContent>
      <Typography
        className="cannot-redeem-text"
        dangerouslySetInnerHTML={{
          __html: sanitizeHtml(t('cannotRedeemReferralRewardDesc')),
        }}
      />
    </StyledReferralCannotRedeemContent>
  );

  useEffect(() => {
    setValue(maxRewardCharacter);
  }, [maxRewardCharacter]);

  return (
    <>
      <Box
        className="gift-button"
        variant="contained"
        onClick={handleClickGiftButton}
      >
        <CommonButton
          variant="contained"
          textProps={{
            color: COLOR.darkBlue,
            variantText: 'body-14-semibold',
          }}
          disabled={!initPoints}
          customStyle={{
            height: '40px',
            borderRadius: '8px',
          }}
        >
          {t('claimRewards')}
        </CommonButton>
      </Box>
      <DialogWithContent
        open={openPopupReward}
        onClose={handleClosePopupReward}
        contentPopup={contentRewardDialog()}
        image={PaperFireworks}
        title={t('claimCharacterRewardsTitle')}
        actionComponent={renderRedeemAction()}
      />
      <DialogWithContent
        open={openPopupCannotRedeem}
        onClose={handleClosePopupCannotRedeem}
        contentPopup={contentCannotRedeemDialog()}
        title={t('cannotRedeemReferralRewardTitle')}
        image={ReferralErrorIcon}
        actionComponent={renderCannotRedeemAction()}
      />
    </>
  );
};

export default GiftButton;
